import type { ActiveBoost, BoostDef, BoostEffect, Player } from './types';

export const BOOSTS: Record<string, BoostDef> = {
  combat_stim: {
    id: 'combat_stim',
    nameKey: 'combat_stim',
    descriptionKey: 'combat_stim_desc',
    effect: 'damage',
    value: 0.25,
    durationSec: 300,
    cost: { nano_chip: 5 },
  },
  shield_matrix: {
    id: 'shield_matrix',
    nameKey: 'shield_matrix',
    descriptionKey: 'shield_matrix_desc',
    effect: 'defense',
    value: 0.3,
    durationSec: 300,
    cost: { nano_chip: 3, scrap: 10 },
  },
  nano_medkit: {
    id: 'nano_medkit',
    nameKey: 'nano_medkit',
    descriptionKey: 'nano_medkit_desc',
    effect: 'heal',
    value: 1.0, // full heal
    durationSec: 0,
    cost: { energy_cell: 5 },
  },
  lucky_scan: {
    id: 'lucky_scan',
    nameKey: 'lucky_scan',
    descriptionKey: 'lucky_scan_desc',
    effect: 'loot_chance',
    value: 0.5,
    durationSec: 600,
    cost: { data_track: 1 },
  },
};

export function getBoost(id: string): BoostDef | undefined {
  return BOOSTS[id];
}

export function filterActive(boosts: ActiveBoost[], now: number = Date.now()): ActiveBoost[] {
  return boosts.filter((b) => b.expiresAt > now);
}

/**
 * Get multiplier for a specific effect (damage / defense / loot_chance).
 * Returns e.g. 1.25 for +25% damage.
 * Heal is instant and not handled here.
 */
export function boostMultiplier(
  boosts: ActiveBoost[],
  effect: BoostEffect,
  now: number = Date.now(),
): number {
  let m = 1;
  for (const b of boosts) {
    if (b.expiresAt <= now) continue;
    const def = BOOSTS[b.boostId];
    if (!def || def.effect !== effect) continue;
    m += def.value;
  }
  return m;
}

/** Activate a boost → returns new ActiveBoost list plus possible player changes (e.g., heal). */
export function activateBoost(
  player: Player,
  def: BoostDef,
  now: number = Date.now(),
): { player: Player; boosts: ActiveBoost[] } {
  let nextPlayer = player;
  const nextBoosts = filterActive(player.boosts, now);

  if (def.effect === 'heal') {
    // instant effect: heal in place; tracked nowhere (no duration)
    // currentHp is combat-scoped; if we're outside combat, we just set to something reasonable.
    // Actual combat max is derived — we set a generous HP here; combat recomputes on start.
    nextPlayer = { ...player, currentHp: 9999 };
  } else {
    nextBoosts.push({
      boostId: def.id,
      expiresAt: now + def.durationSec * 1000,
    });
  }

  return {
    player: { ...nextPlayer, boosts: nextBoosts },
    boosts: nextBoosts,
  };
}
